import { Preferences } from "@capacitor/preferences";
import { isNativeApp } from "./platform";

// Device-local "my vendors" list for the native launch screen. This is NOT the
// Phase-3 wallet — no credentials are stored, only which vendors this device
// has visited so the launch screen can offer them back. Sessions remain
// ordinary httpOnly customer_session cookies owned by the WebView.

export interface RememberedVendor {
  slug: string;
  orgName: string;
  /**
   * The org's SQUARE mark (its favicon) — the same asset the admin dashboard treats as
   * the org icon. This is what every fixed-size vendor tile should render: `logoUrl` is
   * usually a wide logo+wordmark lockup, which letterboxes to an unreadable strip inside
   * a 24-44px square. Kept separate from logoUrl so entries written by older builds
   * (icon-less) still resolve through vendorIcon() below.
   */
  iconUrl?: string | null;
  logoUrl?: string | null;
  /** Last account ordering page visited for this vendor (fast re-entry). */
  customUrl?: string | null;
  lastUsedAt: string; // ISO
}

const KEY = "fo_vendors";

/** Square mark for a vendor tile: the org favicon, falling back to the wide logo. */
export const vendorIcon = (v: Pick<RememberedVendor, "iconUrl" | "logoUrl">): string | null =>
  v.iconUrl || v.logoUrl || null;

// The simulated-demo org is a sales/reviewer surface, not a supplier relationship — it must
// never persist as a "vendor" on the device (early builds did store it, which is why the
// listing filter below also acts as a purge for that stale data).
//
// ⚠️ ONLY `demo-public` belongs here. Slug `demo` is org 1 "Demo Produce Co" — a REAL
// production tenant (organizations.is_demo = false) that the founder happens to use for
// live testing. Blacklisting it here (2026-09-04) purged a legitimately-added vendor on
// every launch — "my vendor is forgotten on relaunch" (TestFlight feedback, 2026-09-06).
// Never exclude by slug guesswork: the simulated demo is identified by is_demo=true and
// is already skipped at REMEMBER time in NativeShellIntegration (isDemo prop); this list
// exists only to purge entries older builds wrote for demo-public.
const DEMO_SLUGS = new Set(["demo-public"]);

const isValidVendor = (v: any): v is RememberedVendor =>
  !!v &&
  typeof v.slug === "string" && v.slug.trim() !== "" && !DEMO_SLUGS.has(v.slug) &&
  typeof v.orgName === "string" && v.orgName.trim() !== "";

export async function listRememberedVendors(): Promise<RememberedVendor[]> {
  if (!isNativeApp()) return [];
  try {
    const { value } = await Preferences.get({ key: KEY });
    if (!value) return [];
    const parsed = JSON.parse(value);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter(isValidVendor)
      .sort((a, b) => (b.lastUsedAt || "").localeCompare(a.lastUsedAt || ""));
  } catch {
    return [];
  }
}

export async function rememberVendor(vendor: Omit<RememberedVendor, "lastUsedAt">): Promise<void> {
  if (!isNativeApp()) return;
  if (!isValidVendor({ ...vendor, lastUsedAt: "" })) return;
  try {
    const existing = await listRememberedVendors();
    const rest = existing.filter((v) => v.slug !== vendor.slug);
    const merged: RememberedVendor[] = [
      { ...existing.find((v) => v.slug === vendor.slug), ...vendor, lastUsedAt: new Date().toISOString() },
      ...rest,
    ].slice(0, 20);
    await Preferences.set({ key: KEY, value: JSON.stringify(merged) });
  } catch {
    // Preferences failures never break the ordering flow.
  }
}

export async function forgetVendor(slug: string): Promise<void> {
  if (!isNativeApp()) return;
  try {
    const existing = await listRememberedVendors();
    await Preferences.set({ key: KEY, value: JSON.stringify(existing.filter((v) => v.slug !== slug)) });
  } catch {
    // best-effort
  }
}

/** Put back a vendor just forgotten (the launch screen's Undo), in its old place. */
export async function restoreVendor(vendor: RememberedVendor): Promise<void> {
  if (!isNativeApp()) return;
  try {
    const existing = await listRememberedVendors();
    if (existing.some((v) => v.slug === vendor.slug)) return;
    await Preferences.set({ key: KEY, value: JSON.stringify([...existing, vendor].slice(0, 20)) });
  } catch {
    // best-effort
  }
}
